import { useState, useEffect, useCallback } from "react";
import SvgMapper from "../../features/common/SvgMapper";
import { useSelector, useDispatch } from "react-redux";
import { setInterviewModal, setResume, setActiveInterview } from "../../../store/studentPageReducer";
import InterviewSetup from "../InterviewSetup/InterviewSetup";
import { formatDateTime } from "../dateFormat";
import { updateScheduleInterview } from "../../../application/services/scheduleTabService";
import { getActiveInterview } from "../../../application/services/studentDashboard";

export default function StartInterviewCard() {
  const dispatch = useDispatch();
  const { openInterviewModal , activeInterview, resumeUrl } = useSelector((state) => state.studentpage);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [resumeUploaded, setResumeUploaded] = useState(false);
  
  const handleStartInterview = () => {
    dispatch(setInterviewModal(true));
  };

  const uploadResumeToS3 = async (file) => {
    try {
      const formData = new FormData();
      formData.append("folder", "resumes");
      formData.append("file", file);

      const timestamp = Date.now();
      const response = await fetch(
        `https://testservice.kraftshala.com/storage/add-external?timestamp=${timestamp}`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) throw new Error("File upload failed");
      const data = await response.json();
      return data?.url || data?.fileUrl || null;
    } catch (error) {
      console.error("Error uploading resume to S3:", error);
      return null;
    }
  };

  const handleResumeUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      setUploadError("Only PDF files are allowed");
      e.target.value = "";
      return;
    }
    const minSize = 1024;
    if (file.size === 0 || file.size < minSize) {
      setUploadError("File cannot be empty");
      e.target.value = "";
      return;
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setUploadError("File size must be less than 10MB");
      e.target.value = "";
      return;
    }

    setUploadError("");
    setUploading(true);

    try {
      const uploadedUrl = await uploadResumeToS3(file);
      
      if (uploadedUrl) {
        
        dispatch(setResume(uploadedUrl));
        if (activeInterview) {
          const updatedInterview = {
            ...activeInterview,
            resume_url: uploadedUrl
          };
          dispatch(setActiveInterview(updatedInterview));
          
          const interviewId = activeInterview.id || activeInterview.interview_id || activeInterview.schedule_id;
          if (interviewId) {
            try {
              const payload = {
                resume_url: uploadedUrl
              };
              await updateScheduleInterview(interviewId, payload);
            } catch (apiError) {
              console.error("Error updating scheduled interview:", apiError);
            }
          }
        }
        
        setResumeUploaded(true);
      } else {
        setUploadError("Failed to upload resume. Please try again.");
      }
    } catch (error) {
      console.error("Error uploading resume:", error);
      setUploadError("Failed to upload resume. Please try again.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const fetchActiveInterview = useCallback(async () => {
  try {
    if (!activeInterview?.student_email) return;

    const response = await getActiveInterview(
      activeInterview.student_email
    );

    if (response?.data) {
      dispatch(setActiveInterview(response.data));
    }
  } catch (error) {
    console.error("Fetching active interview failed:", error);
  }
}, [activeInterview?.student_email, dispatch]);

useEffect(() => {
  if (activeInterview?.student_email) {
    fetchActiveInterview();
  }
}, []);

useEffect(() => {
  if (!activeInterview?.resume_url) return;

  const isQuestionGenerated =
    activeInterview?.is_question_generated === true ||
    activeInterview?.is_question_generated === "true";

  if (isQuestionGenerated) return;

  const interval = setInterval(() => {
    fetchActiveInterview();
  }, 2 * 60 * 1000);

  return () => clearInterval(interval);
}, [
  activeInterview?.resume_url,
  activeInterview?.is_question_generated,
  fetchActiveInterview,
]);

useEffect(() => {
  const handleFocus = () => {
    fetchActiveInterview();
  };

  window.addEventListener("focus", handleFocus);

  return () => {
    window.removeEventListener("focus", handleFocus);
  };
}, [fetchActiveInterview]);

  if (!activeInterview) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 text-gray-300">
            {SvgMapper.interviewIcon("w-16 h-16")}
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Active Interviews
          </h3>
          <p className="text-gray-600">
            There are no interviews available at the moment. Check back later for new interview opportunities.
          </p>
        </div>
      </div>
    );
  }

  const { 
    interview_name, 
    active_from, 
    active_to, 
    duration, 
    is_active,
    is_question_generated
  } = activeInterview || {};
  
  const hasResume = activeInterview?.resume_url || resumeUrl;
  const isQuestionGenerated = is_question_generated === true || is_question_generated === "true";
  const canStartInterview = hasResume && is_active === true && isQuestionGenerated;

  return (
    <>
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg p-5 text-white relative">
        {hasResume && !isQuestionGenerated && !uploadError && (
          <div className="mb-4 p-3 bg-green-500/20 border border-green-300/30 rounded-lg backdrop-blur-sm">
            <p className="text-white text-sm font-medium text-center">
              ✓ Your resume has been uploaded successfully. Please wait for few minutes to start the interview.
            </p>
          </div>
        )}
        <div className="flex items-center justify-between ">
          <div>
            <h2 className="text-2xl font-bold mb-2">
              🎯 Your Scheduled Interview
            </h2>
            <p className="text-blue-50 mb-4">
              {interview_name} - {formatDateTime(active_from)} — {formatDateTime(active_to)}
            </p>
          </div>

          <div className="text-right ">
            <div className="flex justify-center gap-2">
              <div className="mt-2">
                <label className={`px-6 py-3 rounded-lg font-semibold text-lg transition-colors ${
                  hasResume || uploading
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed opacity-50"
                    : "bg-yellow-400 text-black cursor-pointer hover:bg-yellow-300"
                }`}>
                  {uploading ? "Uploading..." : "Upload Resume"}
                  <input
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    onChange={handleResumeUpload}
                    disabled={uploading || hasResume}
                  />
                </label>
                {uploadError && (
                  <p className="text-red-200 text-xs mt-1">{uploadError}</p>
                )}
              </div>
              <div>
                <button
                  onClick={handleStartInterview}
                  disabled={!canStartInterview}
                  className={`px-6 py-2 rounded-lg font-semibold text-lg flex items-center transition-colors ${
                    canStartInterview
                      ? "bg-white text-blue-600 hover:bg-blue-50 cursor-pointer"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                >
                  {SvgMapper.play("w-5 h-5")}
                  <span>Start Interview</span>
                </button>
                
                <p className="text-blue-100 text-sm mt-2">
                  {duration ? `${duration} minutes` : ""}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {openInterviewModal && <InterviewSetup />}
    </>
  );
}
